/**
 * Error telemetry for Firebase SDK calls.
 *
 * ErrorInterceptor only sees Angular HttpClient traffic, but Echo reaches its
 * backend almost entirely through the Firebase SDK - callables, Firestore
 * listeners, Storage - none of which passes through HttpClient. That left the
 * majority of real backend failures invisible to analytics.
 *
 * The fix is a drop-in replacement for `httpsCallable`: importing it from here
 * instead of from '@angular/fire/functions' instruments every call in a file
 * without touching a single call site.
 *
 *   - import { Functions, httpsCallable } from '@angular/fire/functions';
 *   + import { Functions } from '@angular/fire/functions';
 *   + import { httpsCallable } from '../core/services/telemetry';
 *
 * The reporter is registered once at startup rather than injected, so the
 * wrapper stays a plain function and can be called from field initialisers
 * without needing an injection context.
 */

import {
    Functions,
    httpsCallable as firebaseHttpsCallable,
    HttpsCallable,
    HttpsCallableOptions
} from '@angular/fire/functions';

/** What a failed backend call reports. */
export interface CallableFailure {
    /** The callable's name, e.g. 'wabGetDashboardData'. */
    name: string;
    /** Firebase error code, e.g. 'permission-denied', 'unauthenticated'. */
    code: string;
    message: string;
    /** Milliseconds from call to rejection. */
    durationMs: number;
}

type Reporter = (failure: CallableFailure) => void;

let reporter: Reporter | null = null;

/**
 * Install the failure reporter. Called once from AppComponent - before that,
 * failures pass through unreported rather than queueing, since a failure
 * during bootstrap has no user context to report against anyway.
 */
export function registerCallableReporter(fn: Reporter): void {
    reporter = fn;
}

/**
 * Pull a Firebase error code off whatever the SDK rejected with.
 *
 * @param {any} error - The rejection value.
 * @return {string} A Firebase error code, or 'unknown'.
 */
function errorCode(error: any): string {
    // FunctionsError puts it on `code`, usually as 'functions/internal'.
    const raw = error?.code || error?.name || 'unknown';
    return typeof raw === 'string' ? raw.replace(/^functions\//, '') : 'unknown';
}

/**
 * Drop-in replacement for AngularFire's `httpsCallable` that reports failures.
 *
 * Identical signature and behaviour: the original error is always re-thrown,
 * so callers see exactly what they saw before.
 *
 * @param {Functions} functionsInstance - The Functions instance.
 * @param {string} name - Name of the callable.
 * @param {HttpsCallableOptions} [options] - Callable options.
 * @return {HttpsCallable} An instrumented callable.
 */
export function httpsCallable<RequestData = unknown, ResponseData = unknown>(
    functionsInstance: Functions,
    name: string,
    options?: HttpsCallableOptions
): HttpsCallable<RequestData, ResponseData> {
    const inner = firebaseHttpsCallable<RequestData, ResponseData>(functionsInstance, name, options);

    const wrapped = ((data?: RequestData) => {
        const startedAt = Date.now();
        return inner(data).catch((error: any) => {
            try {
                reporter?.({
                    name,
                    code: errorCode(error),
                    message: error?.message || String(error),
                    durationMs: Date.now() - startedAt
                });
            } catch {
                // Telemetry must never mask the original failure.
            }
            throw error;
        });
    }) as HttpsCallable<RequestData, ResponseData>;

    return wrapped;
}

/**
 * Report a Firestore listener or read failure.
 *
 * Long-lived `onSnapshot` listeners fail silently into a console.error, which
 * means a tenant whose rules or connectivity break shows an empty screen and
 * produces no signal. Pass the listener's error callback through here.
 *
 * @param {string} source - Where it failed, e.g. 'conversations.listener'.
 * @param {any} error - The Firestore error.
 */
export function reportFirestoreError(source: string, error: any): void {
    try {
        reporter?.({
            name: `firestore:${source}`,
            code: errorCode(error),
            message: error?.message || String(error),
            durationMs: 0
        });
    } catch {
        // Ignore - see above.
    }
}
